import React, { memo, useState } from 'react';
import type { Editor } from 'slate';
import { useSlate } from 'slate-react';
import NumberList from '../../assets/RichTeIcons/icon-numbered-list.svg';
import NumberListActive from '../../assets/RichTeIcons/icon-numbered-list-active.svg';
import BulletList from '../../assets/RichTeIcons/icon-bulleted-list.svg';
import BulletListActive from '../../assets/RichTeIcons/icon-bulleted-list-active.svg';
import { useRTEContext } from './RTEContext';
import { ensureSelection, isBlockActive, toggleBlock } from './utils/utils';
import { ToolButtonWrapper, ToolButton, ToolButtonIcon } from './ToolBarStyles';
import ToolTip from './ToolTip';

type BlockButtonProps = {
  activeIcon: string;
  icon: string;
  tooltip: string;
  isActive: boolean;
  'data-testid': string;
  onToggle: () => void;
};

const BlockButton = memo(
  ({
    icon,
    activeIcon,
    tooltip,
    isActive,
    onToggle,
    'data-testid': dataTestId,
  }: BlockButtonProps) => {
    const [tooltipIsVisible, setTooltipIsVisible] = useState<boolean>(false);
    const { state: RTEState } = useRTEContext();
    const { userDisabled: editDisabled } = RTEState;

    return (
      <ToolButtonWrapper>
        <ToolTip text={tooltip} $isVisible={tooltipIsVisible}>
          <ToolButton
            data-testid={dataTestId}
            $isDisabled={editDisabled}
            disabled={editDisabled}
            onClick={onToggle}
            onMouseEnter={() => {
              setTooltipIsVisible(true);
            }}
            onMouseLeave={() => {
              setTooltipIsVisible(false);
            }}
          >
            <ToolButtonIcon
              src={isActive ? activeIcon : icon}
              $isActive={isActive}
              $isDisabled={editDisabled}
            />
          </ToolButton>
        </ToolTip>
      </ToolButtonWrapper>
    );
  }
);

export default (): JSX.Element => {
  const editor: Editor = useSlate();

  return (
    <>
      <BlockButton
        tooltip="Numbered List"
        icon={NumberList}
        activeIcon={NumberListActive}
        data-testid="NumberListButton"
        isActive={isBlockActive(editor, 'numbered-list')}
        onToggle={() => {
          ensureSelection(editor);
          toggleBlock(editor, 'numbered-list');
        }}
      />
      <BlockButton
        tooltip="Bulleted List"
        icon={BulletList}
        activeIcon={BulletListActive}
        data-testid="BulletListButton"
        isActive={isBlockActive(editor, 'bulleted-list')}
        onToggle={() => {
          ensureSelection(editor);
          toggleBlock(editor, 'bulleted-list');
        }}
      />
    </>
  );
};
